import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { NOT_SET } from '../workforce/assumption/recruitmentStatusCategories';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import TableSearchInput from 'CommonComponents/TableSearchInput';

import useDropdown from 'Hooks/useDropdown';

import ChartTableIcon from 'Assets/icons/chart-table.svg';
import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import DotsIcon from 'Assets/icons/dots-horizontal.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export const COMMITTED_ONLY = 'committed';
export const UNCOMMITTED_ONLY = 'uncommitted';

// Paid, Consultant, Secondee, Volunteer; labels come translated from the page.
const ROLE_TYPES = ['P', 'C', 'S', 'V'];

const FILTER_SELECT_CLASS = 'select select-input !tw-bg-white !tw-border !tw-w-auto tw-max-w-xs tw-truncate tw-text-xs';

/** A toolbar button with a panel beneath it, portalled so it escapes the card's overflow. */
export const ToolbarMenu = ({ label, ariaLabel, icon, variant = 'outline', caret = true, panelRole = 'menu', children }) => {
    const { triggerRef, dropdownRef, isOpen, close, toggle } = useDropdown();

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, close]);

    return (
        <div ref={triggerRef} className="tw-inline-flex">
            <Button variant={variant} aria-label={ariaLabel} aria-haspopup={panelRole === 'menu' ? 'menu' : true} aria-expanded={isOpen} onClick={toggle}>
                {icon}
                {label}
                {caret && <ChevronDownIcon className="tw-w-2 tw-h-2" aria-hidden="true" />}
            </Button>
            {isOpen &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        role={panelRole}
                        aria-label={ariaLabel ?? label}
                        className="tw-min-w-[11rem] tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-p-1.5 tw-text-start tw-shadow-primary"
                    >
                        {typeof children === 'function' ? children(close) : children}
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default function PositionsToolbar({
    search,
    onSearchChange,
    departments,
    filters,
    onFilterChange,
    positionTypes,
    statuses,
    showStatusFilter,
    columns,
    columnDefinitions,
    onToggleColumn,
    showDeleteAll,
    onDeleteAll,
}) {
    return (
        <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-items-center tw-gap-2">
                <div className="tw-w-60 tw-shrink-0">
                    <TableSearchInput
                        searchValue={search}
                        handleChange={(e) => onSearchChange(e.target.value)}
                        placeholder={$t('Search position, code, supervisor')}
                    />
                </div>
                <select
                    aria-label={$t('Department')}
                    className={FILTER_SELECT_CLASS}
                    value={filters.department}
                    onChange={(e) => onFilterChange('department', e.target.value)}
                >
                    <option value="">{$t('All departments')}</option>
                    {departments.map((department) => (
                        <option value={department.id} key={department.id}>
                            {department.name}
                        </option>
                    ))}
                </select>
                <select aria-label={$t('Type')} className={FILTER_SELECT_CLASS} value={filters.type} onChange={(e) => onFilterChange('type', e.target.value)}>
                    <option value="">{$t('All types')}</option>
                    {ROLE_TYPES.filter((value) => positionTypes[value]).map((value) => (
                        <option value={value} key={value}>
                            {positionTypes[value]}
                        </option>
                    ))}
                </select>
                {showStatusFilter && (
                    <select
                        aria-label={$t('Recruitment status')}
                        className={FILTER_SELECT_CLASS}
                        value={filters.recruitmentStatus}
                        onChange={(e) => onFilterChange('recruitmentStatus', e.target.value)}
                    >
                        <option value="">{$t('All statuses')}</option>
                        <option value={NOT_SET}>{$t('Not set')}</option>
                        {statuses.map((status) => (
                            <option value={status.id} key={status.id}>
                                {status.name}
                            </option>
                        ))}
                    </select>
                )}
                <select
                    aria-label={$t('Committed')}
                    className={FILTER_SELECT_CLASS}
                    value={filters.committed}
                    onChange={(e) => onFilterChange('committed', e.target.value)}
                >
                    <option value="">{$t('Committed: any')}</option>
                    <option value={COMMITTED_ONLY}>{$t('Committed only')}</option>
                    <option value={UNCOMMITTED_ONLY}>{$t('Uncommitted only')}</option>
                </select>
            </div>

            <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                <ToolbarMenu label={$t('Columns')} icon={<ChartTableIcon className="tw-w-3.5 tw-h-3.5" aria-hidden="true" />} panelRole="group">
                    <div className="tw-px-2 tw-py-1 tw-space-y-2">
                        <div className="tw-text-xs tw-font-semibold tw-text-blue">{$t('Show columns')}</div>
                        {columnDefinitions.map((definition) => (
                            <CheckboxInput
                                key={definition.key}
                                id={`positions-column-${definition.key}`}
                                checked={columns[definition.key]}
                                onChange={() => onToggleColumn(definition.key)}
                                label={<span className="tw-text-xs tw-text-charcoal">{definition.label}</span>}
                            />
                        ))}
                    </div>
                </ToolbarMenu>
                {showDeleteAll && (
                    <ToolbarMenu ariaLabel={$t('More actions')} icon={<DotsIcon className="tw-w-3.5 tw-h-3.5" aria-hidden="true" />} caret={false}>
                        {(close) => (
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    close();
                                    onDeleteAll();
                                }}
                                className="edit-locked-budget-entity tw-flex tw-w-full tw-items-center tw-gap-x-2 tw-rounded tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-red hover:tw-bg-background"
                            >
                                <TrashIcon className="tw-w-3.5 tw-h-3.5" aria-hidden="true" />
                                <span>{$t('Delete all')}</span>
                            </button>
                        )}
                    </ToolbarMenu>
                )}
            </div>
        </div>
    );
}
